import React, { useState, useRef } from 'react'
import './table-import.scss'
import { Column, Row } from '@/components/layout/layout'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Upload } from 'antd'
import Button from '@/components/button/button'
import DownLoadImg from '@/assets/images/table_download_icon.svg'
import { useUploadXlsx } from './useUploadXlsx'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { ColumnsType } from 'antd/lib/table'
import { Table } from 'antd'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'

interface TableImportProps {
    onImport: (recordId: number, cb: () => void) => void
}

export interface ItableKeysMap {
    primary_group_name: any
    secondary_group_name: any
    goods_name: any
    goods_category: any
    purchase_price: any
    first_tier_price: any
    second_tier_price: any
    third_tier_price: any
    member_price: any
    unit_name: any
    stock: any
    goods_code: any
    goods_barcode: any
}

const tableKeysArr = [
    'primary_group_name',
    'secondary_group_name',
    'goods_name',
    'goods_category',
    'purchase_price',
    'first_tier_price',
    'second_tier_price',
    'third_tier_price',
    'member_price',
    'unit_name',
    'stock',
    'goods_code',
    'goods_barcode',
]

const TableImport: React.FC<TableImportProps> = props => {
    const cls = useBemClass('table-import')

    const recordId = useRef<number>(0)

    const [importList, setImportList] = useState<ItableKeysMap[]>([])
    const [hasData, setHasData] = useState<boolean>(false)
    const [uploading, setUploading] = useState<boolean>(false)
    const [importing, setImporting] = useState<boolean>(false)

    const setAllData = useSetDataTogether()

    const { upload: uploadXslx, url: uploadUrl } = useUploadXlsx({
        complete: () => {
            setUploading(false)
        },
    })

    const uploadProps = {
        accept: '.xls,.xlsx,application/vnd.ms-excel',
        showUploadList: false,
        beforeUpload: (file: Blob) => {
            const f = file
            const reader = new FileReader()
            reader.onload = (e: any) => {
                const datas = e.target.result
                import('xlsx').then(XLSX => {
                    const workbook = XLSX.read(datas, {
                        type: 'binary',
                    })
                    const first_worksheet = workbook.Sheets[workbook.SheetNames[0]]
                    const jsonArr = XLSX.utils.sheet_to_json(first_worksheet, { header: 1 })
                    handleImpotedJson(jsonArr, file)
                })
            }
            reader.readAsBinaryString(f)
            uploadXslx(f)
            setUploading(true)

            return false
        },
        onRemove: () => {},
    }

    const handleImpotedJson = (jsonArr: any, file: any) => {
        jsonArr.splice(0, 1) // 去掉表头
        const jsonArrData = jsonArr.map((item: any[]) => {
            let items: {
                [key: string]: any
            } = {}

            item.forEach((i, index) => {
                items[tableKeysArr[index]] = i
            })

            return items
        })

        setAllData(() => {
            setHasData(true)
            setImportList(jsonArrData)
        })
    }

    const importGoodReq = useRequest(
        () =>
            $post({
                url: '/goods/migration/importgoodssheet',
                params: {
                    sheet_url: uploadUrl.current,
                },
            }),
        {
            manual: true,
            onSuccess: res => {
                recordId.current = res.record_id
                props.onImport(recordId.current, () => {
                    setAllData(() => {
                        setImportList([])
                        setHasData(false)
                        setImporting(false)
                    })
                })
            },
            onError: e => $msg('error', e.message),
        },
    )

    const column: ColumnsType<ItableKeysMap> = [
        {
            title: '一级分组',
            dataIndex: 'primary_group_name',
            width: 130,
        },
        {
            title: '二级分组',
            dataIndex: 'secondary_group_name',
            width: 130,
        },
        {
            title: '商品名称',
            dataIndex: 'goods_name',
            width: 130,
        },
        {
            title: '商品类目',
            dataIndex: 'goods_category',
            width: 130,
        },
        {
            title: '进价',
            dataIndex: 'purchase_price',
            render: (val, record) => (val ? `${val}元/${record.unit_name}` : ''),
            width: 130,
        },
        {
            title: '零售价',
            dataIndex: 'first_tier_price',
            render: (val, record) => `${val}元/${record.unit_name}`,
            width: 130,
        },
        {
            title: '二级阶梯价',
            dataIndex: 'second_tier_price',
            width: 130,
            render: (val: string, record) => {
                if (val) {
                    const v = val.split('、')

                    return `${v[0]}元，${v[1]}元/${record.unit_name}`
                }

                return ''
            },
        },
        {
            title: '三级阶梯价',
            dataIndex: 'third_tier_price',
            width: 130,
            render: (val: string, record) => {
                if (val) {
                    const v = val.split('、')

                    return `${v[0]}元，${v[1]}元/${record.unit_name}`
                }

                return ''
            },
        },
        {
            title: '会员价',
            width: 130,
            dataIndex: 'member_price',
            render: (val, record) => (val ? `${val}元/${record.unit_name}` : ''),
        },
        {
            title: '销售单位',
            width: 130,
            dataIndex: 'unit_name',
        },
        {
            title: '商品库存',
            width: 130,
            dataIndex: 'stock',
            render: (val, record) => (val ? `${val}${record.unit_name}` : ''),
        },
        {
            title: '商品编码',
            width: 130,
            dataIndex: 'goods_code',
        },
        {
            title: '商品条码',
            width: 130,
            dataIndex: 'goods_barcode',
        },
    ]

    return (
        <Column data-width-100p data-align-items-center>
            <div className={cls('tip')}>
                <Row data-margin-bottom-16>
                    <div>1.</div>
                    <div data-margin-left-8>
                        目前仅支持上传后缀为.csv的Excel表格文件，请下载系统提供的Excel模板，商品数据量限制在500行内，并且严格遵循规定填写。详细规则见附件
                        <a
                            className="action"
                            href="https://d.senguo.cc/ls/tpl/%E5%95%86%E5%93%81%E6%95%B0%E6%8D%AE%E8%A1%A8%E6%A0%BC%E7%A4%BA%E4%BE%8B.xlsx"
                        >
                            （点击下载:
                            <img src={DownLoadImg} alt="download-icon" />
                            商品数据表格 (示例).xlxs）
                        </a>
                        ，带红色<span data-color-ff4343>*</span>的为必填项。
                    </div>
                </Row>
                <Row data-margin-bottom-16>
                    <div>2.</div>
                    <div data-margin-left-8>若导入重名商品，将不会做数据合并，而是直接创建为新商品。</div>
                </Row>
                <Row data-margin-bottom-16>
                    <div>3.</div>
                    <div data-margin-left-8 data-margin-right-16>
                        若不填写商品分组，则默认将商品置于所有商品组别中；若填写分组不存在，将按先后顺序自动创建新分组，超过分组上限的商品将视为无分组处理；若填写一级分组却填写了二级分组，视同未填写分组处理。
                    </div>
                </Row>
                <Row data-margin-bottom-16>
                    <div>4.</div>
                    <div data-margin-left-8>
                        商品类目可参考《商品类目》中的类目名称，请填写对应中文；暂不支持导入自定义商品图片，商品图片将根据类目名称选择系统默认图片。
                    </div>
                </Row>
                <Row data-margin-bottom-16>
                    <div>5.</div>
                    <div data-margin-left-8>
                        销售单位可填写系统默认单位和已建立的自定义单位，库存单位默认与销售单位相同；若填写的单位不存在，将视同需修改的错误数据。
                    </div>
                </Row>
                <Row data-margin-bottom-16>
                    <div>6.</div>
                    <div data-margin-left-8>
                        阶梯价请按【数量，价格】格式填写，相关单位将自动带出；若未填写一级阶梯价，但填写了二级阶梯价，将视同无阶梯价处理。
                    </div>
                </Row>
                <Row>
                    <div>7.</div>
                    <div data-margin-left-8 data-margin-right-16>
                        若填写了商品库存，则会把库存扣减默认设置为“添加到订单时扣减库存”、“允许库存为负数但弹出警告”，若需修改，请导入成功后，在商品编辑页面逐一设置。
                    </div>
                </Row>
            </div>
            <div className={cls('table')}>
                <Table
                    bordered
                    pagination={false}
                    rowKey={val => val.goods_code}
                    columns={column}
                    loading={uploading}
                    dataSource={importList}
                    scroll={{ y: 226, x: '968px' }}
                />
                {!hasData && (
                    <div className={cls('table', 'upload')}>
                        <Upload {...uploadProps}>
                            <Button types="default" loading={uploading}>
                                上传表格
                            </Button>
                        </Upload>
                    </div>
                )}
                <div className={cls('table', 'bottom')}>
                    {hasData && (
                        <>
                            <Row data-align-items-center data-font-size-14>
                                共
                                <span data-color-green data-margin-h-4>
                                    {importList.length}
                                </span>
                                条数据
                                {/* ，共
                                <span data-color-ff4343 data-margin-h-4>
                                    213
                                </span>
                                条失败数据
                                <img data-margin-left-20 src={DownLoadImg} alt="download-icon" />
                                <div className="action">下载失败数据</div> */}
                            </Row>
                            <Row data-align-items-center>
                                {importing ? (
                                    <div className="des">
                                        导入过程中，您可以放心离开此页面
                                        <br />
                                        将为您保持后台导入进程
                                    </div>
                                ) : (
                                    <Upload {...uploadProps}>
                                        <Button
                                            style={{
                                                color: '#999',
                                            }}
                                            types="primary"
                                        >
                                            重新上传
                                        </Button>
                                    </Upload>
                                )}

                                <Button
                                    types="default"
                                    style={{
                                        marginLeft: '20px',
                                    }}
                                    loading={importing}
                                    onClick={() => {
                                        importGoodReq.run()
                                        setImporting(true)
                                    }}
                                >
                                    {importing ? '导入中' : '开始导入'}
                                </Button>
                            </Row>
                        </>
                    )}
                </div>
            </div>
        </Column>
    )
}

export default TableImport
